/**
 * Date and time fields that read the same in Arabic as in English.
 *
 * The native `<input type="date">` takes its placeholder and field order from
 * the browser's locale. On an Arabic system Chrome draws the empty field as a
 * row of disconnected Arabic letters — one per digit of يوم/شهر/سنة — which is
 * exactly what the QA report (§1.3) found unreadable, and it orders the parts
 * however the locale says rather than the day/month/year the report asks for
 * (§2.3). `type="time"` and `type="datetime-local"` have the same problem.
 *
 * These replace them with a plain text field in a fixed left-to-right
 * dd/mm/yyyy (or HH:MM) shape — the slashes appear as you type, Arabic-Indic
 * digits are accepted — and a picker in a popover. The value they hand back is
 * exactly what the native inputs handed back (`YYYY-MM-DD`, `HH:MM`,
 * `YYYY-MM-DDTHH:MM`) through the same `onChange(event)` shape, so every call
 * site swapped `<Input type="date">` for `<DateInput>` and changed nothing else.
 *
 * The popover renders into document.body, positioned against the field: these
 * fields live inside modals whose bodies scroll, and an absolutely positioned
 * calendar there would be clipped by the modal's overflow.
 */

import { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { resolveIcon } from './icons';
import {
  ARABIC_MONTHS,
  ARABIC_WEEKDAYS_SAT_FIRST,
  formatDmy,
  maskDmy,
  maskTime,
  parseDmy,
  parseTime,
  twelveHour,
} from '../../domain/bidi';

const Glyph = ({ name, size = 16, className = '' }) => {
  const Component = resolveIcon(name);
  return <Component size={size} className={className} aria-hidden="true" />;
};

const pad = (n) => String(n).padStart(2, '0');

/** Full width unless the caller sized the field itself (`w-44`, `w-auto`…). */
const sized = (className) => (/(^|\s)(w-|min-w-|max-w-)/.test(className) ? '' : 'w-full');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayYmd = () => ymd(new Date());

/** An event the existing handlers can read `event.target.value` from. */
const changeEvent = (value, name) => ({ target: { value, name }, currentTarget: { value, name } });

/* ------------------------------------------------------------------------ */
/* The popover                                                              */
/* ------------------------------------------------------------------------ */

/**
 * Anchored under (or, near the bottom of the screen, over) the field, in a
 * portal. Closes on Escape and on a press outside both the field and itself.
 */
function Popover({ anchor, open, onClose, children, width = 300 }) {
  const panel = useRef(null);
  const [style, setStyle] = useState({ visibility: 'hidden' });

  const place = useCallback(() => {
    const el = anchor.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const height = panel.current ? panel.current.offsetHeight : 340;
    const below = window.innerHeight - rect.bottom;
    const top = below < height + 12 && rect.top > height + 12 ? rect.top - height - 6 : rect.bottom + 6;
    // Right-aligned with the field, as an RTL page expects, and kept on screen.
    const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
    setStyle({ position: 'fixed', top, left, width, zIndex: 400 });
  }, [anchor, width]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onMove = () => place();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onDown = (e) => {
      if (panel.current?.contains(e.target) || anchor.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, onClose, anchor]);

  if (!open) return null;
  return createPortal(
    <div ref={panel} style={style} className="picker-pop glass-panel" dir="rtl" role="dialog">
      {children}
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------------ */
/* The calendar                                                             */
/* ------------------------------------------------------------------------ */

function Calendar({ value, min, max, onPick, onClear }) {
  const initial = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date();
  const [view, setView] = useState({ y: initial.getFullYear(), m: initial.getMonth() });

  const days = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    // Saturday-first: JS Sunday=0 … Saturday=6 → column index.
    const lead = (first.getDay() + 1) % 7;
    const count = new Date(view.y, view.m + 1, 0).getDate();
    const cells = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= count; d += 1) cells.push(`${view.y}-${pad(view.m + 1)}-${pad(d)}`);
    return cells;
  }, [view]);

  const move = (delta) =>
    setView((v) => {
      const d = new Date(v.y, v.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });

  const thisYear = new Date().getFullYear();
  const years = [];
  for (let y = thisYear + 10; y >= 1900; y -= 1) years.push(y);
  const today = todayYmd();
  const outOfRange = (day) => (min && day < min) || (max && day > max);

  return (
    <div className="p-3">
      <div className="mb-2 flex items-center gap-1">
        <button type="button" className="picker-nav" onClick={() => move(-1)} aria-label="الشهر السابق">
          <Glyph name="chevron-right" />
        </button>
        <div className="flex flex-1 items-center justify-center gap-1">
          <select
            className="picker-select"
            value={view.m}
            onChange={(e) => setView((v) => ({ ...v, m: Number(e.target.value) }))}
            aria-label="الشهر"
          >
            {ARABIC_MONTHS.map((name, i) => (
              <option key={name} value={i}>
                {name}
              </option>
            ))}
          </select>
          <select
            className="picker-select"
            dir="ltr"
            value={view.y}
            onChange={(e) => setView((v) => ({ ...v, y: Number(e.target.value) }))}
            aria-label="السنة"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="picker-nav" onClick={() => move(1)} aria-label="الشهر التالي">
          <Glyph name="chevron-left" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {ARABIC_WEEKDAYS_SAT_FIRST.map((d) => (
          <span key={d} className="py-1 text-[10px] font-bold text-slate-500">
            {d}
          </span>
        ))}
        {days.map((day, i) =>
          day ? (
            <button
              key={day}
              type="button"
              disabled={outOfRange(day)}
              onClick={() => onPick(day)}
              className={[
                'picker-day',
                day === value && 'picker-day--selected',
                day === today && 'picker-day--today',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-label={formatDmy(day)}
              aria-pressed={day === value}
            >
              {Number(day.slice(8))}
            </button>
          ) : (
            <span key={`blank-${i}`} />
          ),
        )}
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-white/5 pt-2">
        <button
          type="button"
          className="picker-link"
          disabled={outOfRange(today)}
          onClick={() => onPick(today)}
        >
          <Glyph name="calendar-check" size={14} /> اليوم
        </button>
        {onClear && (
          <button type="button" className="picker-link picker-link--muted" onClick={onClear}>
            <Glyph name="x" size={14} /> مسح
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* DateInput                                                                */
/* ------------------------------------------------------------------------ */

/**
 * @param {string}   value     'YYYY-MM-DD' or ''
 * @param {function} onChange  receives { target: { value: 'YYYY-MM-DD' | '' } }
 */
export const DateInput = forwardRef(function DateInput(
  { value = '', onChange, min, max, disabled = false, className = '', name, id, required, placeholder, clearable = true, ...rest },
  ref,
) {
  const wrap = useRef(null);
  const [text, setText] = useState(formatDmy(value));
  const [open, setOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const autoId = useId();

  // Follow the value when it changes from outside (a reset, a loaded record).
  useEffect(() => {
    setText(formatDmy(value));
    setInvalid(false);
  }, [value]);

  const emit = (next) => onChange?.(changeEvent(next, name));

  const typed = (event) => {
    const masked = maskDmy(event.target.value);
    setText(masked);
    if (!masked) {
      setInvalid(false);
      if (value) emit('');
      return;
    }
    if (masked.length === 10) {
      const iso = parseDmy(masked);
      const outside = iso && ((min && iso < min) || (max && iso > max));
      setInvalid(!iso || Boolean(outside));
      if (iso && !outside && iso !== value) emit(iso);
    } else {
      setInvalid(false);
    }
  };

  const blur = () => {
    // An unfinished date is put back to the last good one rather than left
    // half-typed on screen looking saved.
    if (text && text.length < 10) {
      setText(formatDmy(value));
      setInvalid(false);
    }
  };

  const pick = (iso) => {
    setOpen(false);
    setInvalid(false);
    setText(formatDmy(iso));
    if (iso !== value) emit(iso);
  };

  return (
    <div ref={wrap} className={`dt-field ${sized(className)} ${invalid ? 'dt-field--invalid' : ''} ${disabled ? 'opacity-60' : ''} ${className}`}>
      <input
        ref={ref}
        id={id || autoId}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        dir="ltr"
        className="dt-field__input"
        placeholder={placeholder || 'DD/MM/YYYY'}
        value={text}
        onChange={typed}
        onBlur={blur}
        onKeyDown={(e) => e.key === 'ArrowDown' && setOpen(true)}
        disabled={disabled}
        required={required}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      <button
        type="button"
        className="dt-field__button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label="اختيار التاريخ من التقويم"
        aria-expanded={open}
      >
        <Glyph name="calendar-days" size={17} />
      </button>
      <Popover anchor={wrap} open={open} onClose={() => setOpen(false)}>
        <Calendar value={value} min={min} max={max} onPick={pick} onClear={clearable && !required ? () => pick('') : null} />
      </Popover>
    </div>
  );
});

/* ------------------------------------------------------------------------ */
/* TimeInput                                                                */
/* ------------------------------------------------------------------------ */

const HOURS = Array.from({ length: 24 }, (_, h) => pad(h));
const MINUTES = Array.from({ length: 12 }, (_, i) => pad(i * 5));

/**
 * @param {string}   value     'HH:MM' (24-hour) or ''
 * @param {function} onChange  receives { target: { value: 'HH:MM' | '' } }
 */
export const TimeInput = forwardRef(function TimeInput(
  { value = '', onChange, disabled = false, className = '', name, id, required, placeholder, ...rest },
  ref,
) {
  const wrap = useRef(null);
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const autoId = useId();

  useEffect(() => {
    setText(value || '');
    setInvalid(false);
  }, [value]);

  const emit = (next) => onChange?.(changeEvent(next, name));

  const typed = (event) => {
    const masked = maskTime(event.target.value);
    setText(masked);
    if (!masked) {
      setInvalid(false);
      if (value) emit('');
      return;
    }
    if (masked.length === 5) {
      const t = parseTime(masked);
      setInvalid(!t);
      if (t && t !== value) emit(t);
    } else {
      setInvalid(false);
    }
  };

  const blur = () => {
    if (text && text.length < 5) {
      setText(value || '');
      setInvalid(false);
    }
  };

  const [hour, minute] = (value || '').split(':');
  const choose = (h, m) => {
    const next = `${h ?? hour ?? '09'}:${m ?? minute ?? '00'}`;
    setText(next);
    setInvalid(false);
    if (next !== value) emit(next);
  };

  return (
    <div ref={wrap} className={`dt-field dt-field--time ${sized(className)} ${invalid ? 'dt-field--invalid' : ''} ${disabled ? 'opacity-60' : ''} ${className}`}>
      <input
        ref={ref}
        id={id || autoId}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        dir="ltr"
        className="dt-field__input"
        placeholder={placeholder || 'HH:MM'}
        value={text}
        onChange={typed}
        onBlur={blur}
        onKeyDown={(e) => e.key === 'ArrowDown' && setOpen(true)}
        disabled={disabled}
        required={required}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      {value && <span className="dt-field__hint">{twelveHour(value)}</span>}
      <button
        type="button"
        className="dt-field__button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label="اختيار الوقت"
        aria-expanded={open}
      >
        <Glyph name="clock" size={17} />
      </button>
      <Popover anchor={wrap} open={open} onClose={() => setOpen(false)} width={286}>
        <div className="p-3">
          <p className="mb-1.5 text-[11px] font-bold text-slate-400">الساعة</p>
          <div className="grid grid-cols-6 gap-1" dir="ltr">
            {HOURS.map((h) => (
              <button
                key={h}
                type="button"
                className={`picker-day ${h === hour ? 'picker-day--selected' : ''}`}
                onClick={() => choose(h, undefined)}
                title={twelveHour(`${h}:00`)}
              >
                {h}
              </button>
            ))}
          </div>
          <p className="mb-1.5 mt-3 text-[11px] font-bold text-slate-400">الدقيقة</p>
          <div className="grid grid-cols-6 gap-1" dir="ltr">
            {MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                className={`picker-day ${m === minute ? 'picker-day--selected' : ''}`}
                onClick={() => {
                  choose(undefined, m);
                  setOpen(false);
                }}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </Popover>
    </div>
  );
});

/* ------------------------------------------------------------------------ */
/* DateTimeInput                                                            */
/* ------------------------------------------------------------------------ */

/**
 * @param {string}   value     'YYYY-MM-DDTHH:MM' (datetime-local's shape) or ''
 * @param {function} onChange  receives { target: { value } } in the same shape
 */
export function DateTimeInput({ value = '', onChange, disabled, className = '', name, min, max, required }) {
  const [datePart, timePart] = String(value || '').split('T');
  const [date, setDate] = useState(datePart || '');
  const [time, setTime] = useState((timePart || '').slice(0, 5));

  useEffect(() => {
    const [d, t] = String(value || '').split('T');
    setDate(d || '');
    setTime((t || '').slice(0, 5));
  }, [value]);

  const emit = (d, t) => {
    if (!d) {
      if (value) onChange?.(changeEvent('', name));
      return;
    }
    onChange?.(changeEvent(`${d}T${t || '00:00'}`, name));
  };

  return (
    <div className={`grid grid-cols-[1fr_auto] gap-2 ${className}`}>
      <DateInput
        value={date}
        min={min ? String(min).slice(0, 10) : undefined}
        max={max ? String(max).slice(0, 10) : undefined}
        disabled={disabled}
        required={required}
        onChange={(e) => {
          setDate(e.target.value);
          emit(e.target.value, time);
        }}
      />
      <TimeInput
        value={time}
        disabled={disabled}
        className="w-36"
        onChange={(e) => {
          setTime(e.target.value);
          emit(date, e.target.value);
        }}
      />
    </div>
  );
}
